import { useCallback, useEffect, useRef, useState } from 'react';
import { website as Api, type EditableField } from '@tvx/api-client';

/**
 * The page as a visitor gets it, rendered by the server, with its editable
 * wording made editable in place.
 *
 * The preview stays server-rendered on purpose. It is the same `sitePage()`
 * that serves the real page, so what an operator checks is what a visitor
 * receives — a React reimplementation of the page would be a second opinion
 * about what the site looks like, and the two would drift.
 *
 * What React owns is the draft: typing in the preview updates state here, and
 * state is what gets saved.
 */
export function usePreview({ page, fields, hidden, order = [], fieldSpec, onEdit }: {
  page: string;
  fields: Record<string, string>;
  hidden: string[];
  order?: string[];
  fieldSpec: EditableField[];
  onEdit: (key: string, value: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [previewing, setPreviewing] = useState(false);
  /* The draft is read when a render happens, not when one is scheduled — a
     stale closure here would post the fields as they were three keystrokes
     ago and the preview would lag behind the boxes. */
  const latest = useRef({ fields, hidden, order, fieldSpec, onEdit });
  latest.current = { fields, hidden, order, fieldSpec, onEdit };

  const render = useCallback(async () => {
    const frame = frameRef.current;
    if (!frame) return;
    setPreviewing(true);
    try {
      const html = await Api.preview({
        page,
        fields: latest.current.fields,
        hidden: latest.current.hidden,
        sections: Object.fromEntries(latest.current.hidden.map((id) => [id, true])),
        order: latest.current.order,
      });
      const doc = frame.contentDocument;
      if (!doc) return;
      doc.open(); doc.write(html); doc.close();
      makeEditable(doc, latest.current.fieldSpec, (k, v) => latest.current.onEdit(k, v));
    } catch { /* the panel shows the error; the frame keeps the last good page */ }
    finally { setPreviewing(false); }
  }, [page]);

  /* Re-render when the page changes, and when a band is hidden
     swapped — both change the page's shape rather than its words, so they
     cannot be applied to the open document in place. */
  useEffect(() => { void render(); }, [render, hidden.join('|'), order.join('|')]);

  return { frameRef, refresh: render, previewing };
}

/** Turn every marked region into something you can type into. */
function makeEditable(
  doc: Document,
  spec: EditableField[],
  onEdit: (key: string, value: string) => void,
) {
  const byKey = new Map(spec.map((f) => [f.key, f]));
  const marks = [...doc.querySelectorAll<HTMLElement>('[data-tvx-field]')];

  for (const el of marks) {
    const key = el.getAttribute('data-tvx-field');
    if (!key) continue;
    const field = byKey.get(key);
    el.title = `${field?.label ?? key} — click to edit`;
    /* plaintext-only where the browser has it: pasting a heading out of Word
       should not paste Word's markup onto the site. */
    el.setAttribute('contenteditable', 'plaintext-only');
    if (el.contentEditable !== 'plaintext-only') el.setAttribute('contenteditable', 'true');
    el.style.outline = '1px dashed rgba(255,216,77,.45)';
    el.style.outlineOffset = '2px';

    el.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = (e as ClipboardEvent).clipboardData?.getData('text/plain') ?? '';
      doc.execCommand('insertText', false, text.replace(/\s+/g, ' '));
    });
    el.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' && !field?.multiline) { e.preventDefault(); el.blur(); }
      if (ke.key === 'Escape') el.blur();
    });
    el.addEventListener('input', () => {
      const value = el.textContent ?? '';
      onEdit(key, value);
      // The same wording can appear twice on a page; keep the copies in step.
      for (const other of marks) {
        if (other !== el && other.getAttribute('data-tvx-field') === key) other.textContent = value;
      }
    });
    el.addEventListener('blur', () => {
      /* Emptying a region means "use the page's own wording again", so the
         wording comes straight back rather than leaving a hole that looks like
         a bug. */
      if ((el.textContent ?? '').trim() !== '') return;
      const back = field?.fallback ?? '';
      onEdit(key, '');
      for (const other of marks) {
        if (other.getAttribute('data-tvx-field') === key) other.textContent = back;
      }
    });
  }
}
